import React from 'react';

export default function Pagination({ 
  currentPage, 
  totalPages, 
  limit, 
  onPageChange, 
  onLimitChange, 
  total,
  isLoading 
}) {
  if (totalPages <= 1) return null;

  const limits = [10, 25, 50, 100];
  const startItem = (currentPage - 1) * limit + 1;
  const endItem = Math.min(currentPage * limit, total);

  return (
    <div className="d-flex align-items-center justify-content-between py-3 px-2 border-top mt-4" 
         style={{ backgroundColor: '#f8f9fa', borderRadius: '4px' }}>
      {/* Left: Items count and per-page selector */}
      <div className="d-flex align-items-center gap-3">
        <small className="text-muted">
          Showing <strong>{startItem}</strong> to <strong>{endItem}</strong> of <strong>{total}</strong> items
        </small>
        <div className="d-flex align-items-center gap-2">
          <label htmlFor="limit-select" className="text-muted small mb-0">
            Per page:
          </label>
          <select
            id="limit-select"
            className="form-select form-select-sm"
            value={limit}
            onChange={(e) => onLimitChange(parseInt(e.target.value))}
            disabled={isLoading}
            style={{ width: '70px' }}
          >
            {limits.map(l => (
              <option key={l} value={l}>{l}</option>
            ))}
          </select>
        </div>
      </div>

      {/* Right: Page navigation */}
      <nav aria-label="Page navigation">
        <ul className="pagination pagination-sm mb-0" style={{ gap: '4px' }}>
          <li className={`page-item ${currentPage === 1 ? 'disabled' : ''}`}>
            <button
              className="page-link"
              onClick={() => onPageChange(1)}
              disabled={currentPage === 1 || isLoading}
              title="First page"
            >
              «
            </button>
          </li>
          <li className={`page-item ${currentPage === 1 ? 'disabled' : ''}`}>
            <button
              className="page-link"
              onClick={() => onPageChange(currentPage - 1)}
              disabled={currentPage === 1 || isLoading}
              title="Previous page"
            >
              ‹
            </button>
          </li>

          {/* Page numbers */}
          {(() => {
            const pages = [];
            const maxVisible = 5;
            let startPage = Math.max(1, currentPage - Math.floor(maxVisible / 2));
            let endPage = Math.min(totalPages, startPage + maxVisible - 1);
            if (endPage - startPage + 1 < maxVisible) {
              startPage = Math.max(1, endPage - maxVisible + 1);
            }

            if (startPage > 1) {
              pages.push(
                <li key="ellipsis-start" className="page-item disabled">
                  <span className="page-link">…</span>
                </li>
              );
            }

            for (let i = startPage; i <= endPage; i++) {
              pages.push(
                <li key={i} className={`page-item ${currentPage === i ? 'active' : ''}`}>
                  <button
                    className="page-link"
                    onClick={() => onPageChange(i)}
                    disabled={isLoading}
                  >
                    {i}
                  </button>
                </li>
              );
            }

            if (endPage < totalPages) {
              pages.push(
                <li key="ellipsis-end" className="page-item disabled">
                  <span className="page-link">…</span>
                </li>
              );
            }

            return pages;
          })()}

          <li className={`page-item ${currentPage === totalPages ? 'disabled' : ''}`}>
            <button
              className="page-link"
              onClick={() => onPageChange(currentPage + 1)}
              disabled={currentPage === totalPages || isLoading}
              title="Next page"
            >
              ›
            </button>
          </li>
          <li className={`page-item ${currentPage === totalPages ? 'disabled' : ''}`}>
            <button
              className="page-link"
              onClick={() => onPageChange(totalPages)}
              disabled={currentPage === totalPages || isLoading}
              title="Last page"
            >
              »
            </button>
          </li>
        </ul>
      </nav>
    </div>
  );
}
